/* eslint-disable react/no-this-in-sfc */
/* eslint-disable no-template-curly-in-string */
import React, { FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useAppDispatch } from "src/store";
import { useSelector } from "react-redux";
import type { RootState } from "@redux/rootReducer";
import { fetchDashboardChart, DashboardChartType } from "@redux/dashboardSlice";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Spin } from "antd";
import { NewMemberChartItem, NewMemberChart } from "@api/dashboardApi";
import PageTitle from "@component/PageTitle";
import ChartFilter from "../ChartFilter";
import {
  getPointStart,
  getPointInterval,
  getTimeFormat,
  getTooltipTitle,
  getFilledTimeData,
  getTimeRangeText,
} from "../utils";

const Wrapper = styled.div`
  padding: 10px 25px;
`;
const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
  padding: 30px;
  min-height: 700px;
`;

const NewMemberCountChart: FC = () => {
  const dispatch = useAppDispatch();
  const { isFetchingChart, chartResult, chartParams } = useSelector((state: RootState) => state.dashboard);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>();

  useEffect(() => {
    const { startDate, endDate, cStartDate, cEndDate, timeUnit } = chartParams;
    const pointStart = getPointStart(startDate, timeUnit);
    const pointInterval = getPointInterval(timeUnit);
    const filledCurrentTimeMap = getFilledTimeData<NewMemberChartItem>(
      startDate,
      endDate,
      (chartResult as NewMemberChart).currentData,
      timeUnit,
    );
    const filledCompareTimeMap = getFilledTimeData<NewMemberChartItem>(
      cStartDate,
      cEndDate,
      (chartResult as NewMemberChart).compareData || [],
      timeUnit,
    );
    const currentMemberCount: (number | null)[] = [];
    const compareMemberCount: (number | null)[] = [];
    const currentTimes = Array.from(filledCurrentTimeMap.keys());
    const compareTimes = Array.from(filledCompareTimeMap.keys());
    const currentFilledTimeData = Array.from(filledCurrentTimeMap.values());
    const compareFilledTimeData = Array.from(filledCompareTimeMap.values());

    currentFilledTimeData.forEach((data) => {
      const { registerMember = null } = data;
      currentMemberCount.push(registerMember);
    });
    compareFilledTimeData.forEach((data) => {
      const { registerMember = null } = data;
      compareMemberCount.push(registerMember);
    });

    const currentTimeRangeText = getTimeRangeText(startDate, endDate, timeUnit);
    const compareTimeRangeText = getTimeRangeText(cStartDate, cEndDate, timeUnit);

    const series: Highcharts.SeriesOptionsType[] = [
      {
        name: currentTimeRangeText,
        data: currentMemberCount,
        type: "line",
        color: "#5C6AC4",
        stack: "current",
        pointStart,
      },
    ];

    if (cStartDate && cEndDate) {
      series.push({
        name: compareTimeRangeText,
        data: compareMemberCount,
        type: "line",
        color: "#1890FF",
        stack: "compare",
        pointStart,
      });
    }

    setChartOptions({
      title: {
        text: "",
      },
      chart: {
        type: "line",
      },
      xAxis: {
        type: "datetime",
        labels: {
          formatter() {
            const timeFormat = getTimeFormat(timeUnit);
            const formattedText = Highcharts.dateFormat(timeFormat, this.value as number);
            return formattedText;
          },
          rotation: -45,
        },
      },
      yAxis: {
        title: {
          text: "",
        },
      },
      tooltip: {
        formatter() {
          const pointIndex = this.point.index;
          const stackName = this.point.series.userOptions.stack;
          const title =
            stackName === "current"
              ? getTooltipTitle(startDate, endDate, currentTimes[pointIndex], timeUnit)
              : getTooltipTitle(cStartDate, cEndDate, compareTimes[pointIndex], timeUnit);
          const value = this.point.y;

          return `${title}<br/>新註冊會員人數 ${value}<br/>`;
        },
        backgroundColor: "#5D5954",
        borderColor: "#5D5954",
        style: {
          color: "#FFFFFF",
          fontSize: "12px",
          lineHeight: "22px",
        },
        // useHTML: true,
      },
      legend: {
        align: "right",
        verticalAlign: "middle",
        layout: "vertical",
      },
      plotOptions: {
        series: {
          pointStart,
          pointInterval,
        },
      },
      series,
    });
  }, [chartResult, chartParams]);

  const fetchNewMemberChart = useCallback(() => {
    dispatch(fetchDashboardChart(DashboardChartType.NEW_MEMBER));
  }, [dispatch]);

  return (
    <Wrapper>
      <ChartWrapper>
        <PageTitle title="Dashboard - 新註冊會員人數" />
        <Spin spinning={isFetchingChart}>
          <ChartFilter title="新註冊會員人數" fetchAction={fetchNewMemberChart} />
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: 500, marginBottom: 40 } }}
          />
        </Spin>
      </ChartWrapper>
    </Wrapper>
  );
};

export default NewMemberCountChart;
